/**
 * チバリヨ モバイルシェル
 * Phase 2-B-3.3 改修:
 *   - S2 から日報の編集・削除導線 (onEditReport → S3 編集モードサブ画面)
 * Phase 2-B-3.2 改修:
 *   - S5 KPI 本実装 (mobile-s5-kpi.js をマウント)
 * Phase 2-B-3.1 改修:
 *   - S1 ホーム本実装 (mobile-s1-home.js をマウント)
 *   - S1 → S4 / S2 へのタブ遷移コールバック
 * Phase 2-B-3 改修:
 *   - S4 タスク一覧本実装 (mobile-s4-tasks.js をマウント)
 *   - S4 から編集サブ画面 (mobile-s4-create.js の編集モード + 削除)
 * Phase 2-B-2 改修:
 *   - S2 タイムライン本実装 (mobile-s2-timeline.js をマウント)
 *   - S2 タブに未読バッジ (localStorage 既読 / 起動時 + タブタップ + 60秒ポーリング + フォーカス復帰)
 * Phase 2-B-1.1 改修:
 *   - S3 保存後に「タスクを続けて作成」する連鎖フロー対応
 *   - openS4CreateSubscreen({ initialClient }) で顧客引き継ぎ
 *
 * sales ロールでログインした時のスマホ向けシェル。
 * - ヘッダー (ブランド + DEVバッジ + アバター)
 * - 下部5タブナビ (S1 ホーム / S2 タイムライン / + / S4 タスク / S5 KPI)
 * - 各タブはプレースホルダー (次フェーズで本実装)
 * - 中央 + は S6 ボトムシート -> S3 日報入力 / S4 タスク作成
 * - サブ画面メカニズム (フルスクリーン画面)
 * - トースト
 */

import { signOutChibariyo } from './auth.js';
import { openS6Modal } from './mobile-s6-modal.js';
import { mountS2Timeline, getS2UnreadCount, markS2Read } from './mobile-s2-timeline.js';
import { mountS4Tasks } from './mobile-s4-tasks.js';
import { mountS1Home } from './mobile-s1-home.js';
import { mountS5Kpi } from './mobile-s5-kpi.js';

const AVATAR_COLORS = {
  blue:   { text: '#0C447C', bg: '#E6F1FB' },
  teal:   { text: '#0F6E56', bg: '#E1F5EE' },
  amber:  { text: '#854F0B', bg: '#FAEEDA' },
  purple: { text: '#3C3489', bg: '#EEEDFE' },
  coral:  { text: '#A32D2D', bg: '#FCEBEB' },
  slate:  { text: '#475569', bg: '#F1F5F9' },
  rose:   { text: '#9F1239', bg: '#FFE4E6' },
  indigo: { text: '#3730A3', bg: '#E0E7FF' },
};

const TAB_DEFS = [
  {
    id: 's1',
    label: 'ホーム',
    title: 'S1 ホーム',
    desc: '今日の動きを5秒で把握する画面。アラート、今日のタスク、今月の進捗などを表示予定。',
    iconPath: 'M3 12L12 4l9 8M5 10v10h14V10',
  },
  {
    id: 's2',
    label: 'タイムライン',
    title: 'S2 日報タイムライン',
    desc: 'SNS的なフィード形式で日報を閲覧する画面。',
    iconPath: 'M4 6h16M4 12h16M4 18h16',
  },
  {
    id: 's6',
    label: '',
    isFab: true,
    iconPath: 'M12 5v14M5 12h14',
  },
  {
    id: 's4',
    label: 'タスク',
    title: 'S4 タスク',
    desc: '自分のタスク一覧。期限超過、本日、明日以降の3層構造。',
    iconPath: 'M5 12l5 5L20 7',
  },
  {
    id: 's5',
    label: 'KPI',
    title: 'S5 KPI',
    desc: '自分のKPI達成率。月次/半期の大型カードと推移チャートを表示予定。',
    iconPath: 'M4 19V9M10 19V5M16 19v-7M22 19v-3',
  },
];

const BACK_ICON_PATH = 'M15 18l-6-6 6-6';

let currentMember = null;
let shellOpts = {};
let activeSubscreen = null;
let activeTabId = null;
let s1Controller = null;
let s2Controller = null;
let s4Controller = null;
let s5Controller = null;
let s2BadgeCount = 0;
let s2BadgeTimer = null;

export function renderMobileShell(rootEl, member, env, opts = {}) {
  currentMember = member;
  shellOpts = opts || {};
  s1Controller = null;
  s2Controller = null;
  s4Controller = null;
  s5Controller = null;
  rootEl.innerHTML = buildShellHTML(env === 'dev', !!shellOpts.showPcSwitch, {
    hideFab: !!shellOpts.hideFab,
    previewBadge: shellOpts.previewBadge || null,
  });
  bindEvents();
  applyAvatar(member);
  switchTab('s1');
  startS2BadgeWatcher();
}

function buildShellHTML(isDev, showPcSwitch, extra = {}) {
  const hideFab = !!extra.hideFab;
  const previewBadge = extra.previewBadge || null;

  const tabsHTML = TAB_DEFS
    .filter((t) => !(hideFab && t.isFab))
    .map((t) => {
    if (t.isFab) {
      return `
        <button class="mobile-tab mobile-tab-fab" data-tab="${t.id}" aria-label="新規作成">
          <div class="mobile-fab-circle">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="${t.iconPath}"/></svg>
          </div>
        </button>
      `;
    }
    return `
      <button class="mobile-tab" data-tab="${t.id}">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="${t.iconPath}"/></svg>
        <span class="mobile-tab-label">${t.label}</span>
        ${t.id === 's2' ? '<span class="mobile-tab-badge" id="s2-tab-badge" style="display:none;"></span>' : ''}
      </button>
    `;
  }).join('');

  const contentsHTML = TAB_DEFS
    .filter((t) => !t.isFab)
    .map((t) => `<div class="mobile-tab-content" data-tab="${t.id}" style="display:none;"></div>`)
    .join('');

  return `
    <div class="mobile-app-frame">
      <header class="mobile-header">
        <div class="mobile-header-brand">
          チバリヨ
          <span class="env-badge-mobile ${isDev ? 'is-visible' : ''}">DEV</span>
          ${previewBadge ? `<span class="mobile-preview-badge">${previewBadge}</span>` : ''}
        </div>
        <div class="mobile-header-actions" style="display:flex;align-items:center;gap:8px;">
          ${showPcSwitch ? `
          <button class="mobile-pc-switch" id="mobile-pc-switch-btn" type="button" aria-label="PC画面に切り替え">
            PC画面
          </button>` : ''}
          <button class="mobile-user-btn" id="mobile-user-btn" aria-label="アカウントメニュー">
            <div class="mobile-user-avatar" id="mobile-user-avatar">?</div>
          </button>
        </div>
      </header>

      <main class="mobile-main">
        ${contentsHTML}
        <div class="mobile-app-footer">
          <img src="/icons/dotarcs-logo.png" alt="ドット・アークス" class="mobile-app-footer-logo"
               width="666" height="140">
        </div>
      </main>

      <nav class="mobile-tabbar" role="tablist">
        ${tabsHTML}
      </nav>
    </div>
    <div class="mobile-toast-wrap" id="mobile-toast-wrap"></div>
  `;
}

function bindEvents() {
  document.querySelectorAll('.mobile-tab').forEach((btn) => {
    btn.addEventListener('click', () => {
      const tabId = btn.dataset.tab;
      switchTab(tabId);
    });
  });

  const userBtn = document.getElementById('mobile-user-btn');
  if (userBtn) {
    userBtn.addEventListener('click', async () => {
      const confirmed = window.confirm(`${currentMember.name}\nログアウトしますか?`);
      if (confirmed) {
        try {
          await signOutChibariyo();
        } catch (err) {
          console.error('[チバリヨ] サインアウト失敗:', err);
          alert('サインアウトに失敗しました: ' + err.message);
        }
      }
    });
  }

  // Phase 2-B-2.1: admin 用 PC画面切替 (location.reload で再起動するため後始末は最小限)
  const pcSwitchBtn = document.getElementById('mobile-pc-switch-btn');
  if (pcSwitchBtn && typeof shellOpts.onSwitchToPc === 'function') {
    pcSwitchBtn.addEventListener('click', () => {
      if (s2BadgeTimer) clearInterval(s2BadgeTimer);
      shellOpts.onSwitchToPc();
    });
  }
}

function applyAvatar(member) {
  const av = document.getElementById('mobile-user-avatar');
  if (!av) return;
  const palette = AVATAR_COLORS[member.avatarColor] || AVATAR_COLORS.slate;
  av.textContent = (member.name || '?').slice(0, 1);
  av.style.color = palette.text;
  av.style.backgroundColor = palette.bg;
}

function switchTab(tabId, opts = {}) {
  // 中央 + は S6 ボトムシート
  if (tabId === 's6') {
    openS6Modal({
      onSelectReport: () => {
        openS3CreateSubscreen();
      },
      onSelectTask: () => {
        openS4CreateSubscreen();
      },
    });
    return;
  }

  activeTabId = tabId;

  document.querySelectorAll('.mobile-tab').forEach((btn) => {
    btn.classList.toggle('is-active', btn.dataset.tab === tabId);
  });

  document.querySelectorAll('.mobile-tab-content').forEach((el) => {
    el.style.display = el.dataset.tab === tabId ? 'block' : 'none';
  });

  if (tabId === 's1') {
    activateS1Tab(opts);
    return;
  }

  if (tabId === 's2') {
    activateS2Tab(opts);
    return;
  }

  if (tabId === 's4') {
    activateS4Tab(opts);
    return;
  }

  if (tabId === 's5') {
    activateS5Tab(opts);
    return;
  }

  renderTabPlaceholder(tabId);
}

/** S1 タブ表示: ホームのマウント / 再表示 (毎回リロード: クエリが軽量) */
function activateS1Tab(opts = {}) {
  const el = document.querySelector('.mobile-tab-content[data-tab="s1"]');
  if (!el) return;

  if (!s1Controller) {
    el.innerHTML = '';
    s1Controller = mountS1Home(el, {
      currentMember,
      showToast,
      onGoToTasks: () => switchTab('s4'),
      onGoToTimeline: () => switchTab('s2'),
    });
  } else {
    s1Controller.onShow({ reload: true });
  }
}

/** S2 タブ表示: タイムラインのマウント / 再表示 + 既読化 */
function activateS2Tab(opts = {}) {
  const hadUnread = s2BadgeCount > 0;
  markS2Read(currentMember.memberId);
  setS2Badge(0);

  const el = document.querySelector('.mobile-tab-content[data-tab="s2"]');
  if (!el) return;

  if (!s2Controller) {
    el.innerHTML = '';
    s2Controller = mountS2Timeline(el, {
      currentMember,
      showToast,
      onEditReport: (report) => openS3EditSubscreen(report),
    });
  } else {
    // 新着がある時 or 日報保存直後は先頭から読み直す
    s2Controller.onShow({ reload: hadUnread || !!opts.forceReload });
  }
}

/** S4 タブ表示: タスク一覧のマウント / 再表示 (毎回リロード: 件数が少なく安価) */
function activateS4Tab(opts = {}) {
  const el = document.querySelector('.mobile-tab-content[data-tab="s4"]');
  if (!el) return;

  if (!s4Controller) {
    el.innerHTML = '';
    s4Controller = mountS4Tasks(el, {
      currentMember,
      showToast,
      onEditTask: (task) => openS4EditSubscreen(task),
    });
  } else {
    s4Controller.onShow({ reload: true });
  }
}

/** S5 タブ表示: KPI のマウント / 再表示 (毎回リロード: 半期分でも軽量) */
function activateS5Tab(opts = {}) {
  const el = document.querySelector('.mobile-tab-content[data-tab="s5"]');
  if (!el) return;

  if (!s5Controller) {
    el.innerHTML = '';
    s5Controller = mountS5Kpi(el, {
      currentMember,
      showToast,
    });
  } else {
    s5Controller.onShow({ reload: true });
  }
}

function renderTabPlaceholder(tabId) {
  if (tabId === 's1' || tabId === 's2' || tabId === 's4' || tabId === 's5') return; // 本実装済み
  const el = document.querySelector(`.mobile-tab-content[data-tab="${tabId}"]`);
  if (!el || el.dataset.rendered === '1') return;

  const def = TAB_DEFS.find((t) => t.id === tabId);
  if (!def) return;

  el.innerHTML = `
    <div class="mobile-placeholder">
      <h2>${def.title}</h2>
      <p>${def.desc}</p>
      <p>※ この画面の本実装は次のフェーズで行います。</p>
      <div class="mobile-placeholder-greeting">こんにちは、${currentMember.name}さん</div>
    </div>
  `;
  el.dataset.rendered = '1';
}

// ============================================================
// S2 未読バッジ (起動時 + 60秒ポーリング + フォーカス復帰)
// ============================================================

function setS2Badge(count) {
  s2BadgeCount = count;
  const badge = document.getElementById('s2-tab-badge');
  if (!badge) return;
  if (count > 0) {
    badge.textContent = count > 99 ? '99+' : String(count);
    badge.style.display = '';
  } else {
    badge.style.display = 'none';
  }
}

async function refreshS2Badge() {
  if (!currentMember) return;
  // S2 表示中は常に既読扱い
  if (activeTabId === 's2' && document.visibilityState === 'visible') {
    markS2Read(currentMember.memberId);
    setS2Badge(0);
    return;
  }
  try {
    const n = await getS2UnreadCount(currentMember.memberId);
    setS2Badge(n);
  } catch (e) {
    console.error('[チバリヨ] 未読件数取得失敗:', e);
  }
}

function startS2BadgeWatcher() {
  if (s2BadgeTimer) clearInterval(s2BadgeTimer);
  refreshS2Badge();
  s2BadgeTimer = setInterval(refreshS2Badge, 60000);
  window.removeEventListener('focus', refreshS2Badge);
  window.addEventListener('focus', refreshS2Badge);
}

// ============================================================
// サブ画面メカニズム
// ============================================================

function openSubscreen(title, mountFn) {
  if (activeSubscreen) return;

  const frame = document.querySelector('.mobile-app-frame');
  if (!frame) return;

  const subscreen = document.createElement('div');
  subscreen.className = 'mobile-subscreen';
  subscreen.innerHTML = `
    <header class="mobile-subscreen-header">
      <button type="button" class="mobile-subscreen-back" aria-label="戻る">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="${BACK_ICON_PATH}"/></svg>
      </button>
      <h1 class="mobile-subscreen-title">${title}</h1>
    </header>
    <div class="mobile-subscreen-body"></div>
  `;
  frame.appendChild(subscreen);

  const bodyEl = subscreen.querySelector('.mobile-subscreen-body');
  const backBtn = subscreen.querySelector('.mobile-subscreen-back');

  requestAnimationFrame(() => {
    subscreen.classList.add('is-open');
  });

  const controller = mountFn(bodyEl) || {};

  activeSubscreen = { containerEl: subscreen, controller };

  function tryClose() {
    const isDirty = controller.isDirty && controller.isDirty();
    if (isDirty) {
      const ok = window.confirm('入力内容を破棄してもよろしいですか?');
      if (!ok) return;
    }
    closeSubscreen();
  }

  backBtn.addEventListener('click', tryClose);
}

function closeSubscreen() {
  if (!activeSubscreen) return;
  const { containerEl, controller } = activeSubscreen;
  containerEl.classList.remove('is-open');
  try { controller && controller.destroy && controller.destroy(); } catch (e) { /* noop */ }
  setTimeout(() => {
    if (containerEl.parentNode) containerEl.parentNode.removeChild(containerEl);
    activeSubscreen = null;
  }, 200);
}

// ============================================================
// S3 / S4 作成サブ画面
// ============================================================

async function openS3CreateSubscreen() {
  const { mountS3CreateForm } = await import('./mobile-s3-create.js');

  openSubscreen('日報を書く', (bodyEl) => {
    const ctrl = mountS3CreateForm(bodyEl, {
      currentMember,
      onSaved: (info) => {
        const openTaskAfter = !!(info && info.openTaskAfter);
        const clientForTask = info && info.clientForTask;
        closeSubscreen();
        showToast('日報を保存しました', 'success');
        if (openTaskAfter) {
          // close アニメーションを待ってから S4 を開く
          setTimeout(() => {
            openS4CreateSubscreen({ initialClient: clientForTask });
          }, 220);
        } else {
          switchTab('s2', { forceReload: true });
        }
      },
    });
    return ctrl;
  });
}

/** Phase 2-B-3.3: S2 タイムラインからの日報編集サブ画面 (S3 編集モード) */
async function openS3EditSubscreen(report) {
  const { mountS3CreateForm } = await import('./mobile-s3-create.js');

  openSubscreen('日報を編集', (bodyEl) => {
    const ctrl = mountS3CreateForm(bodyEl, {
      currentMember,
      initialReport: report,
      onSaved: () => {
        closeSubscreen();
        showToast('日報を更新しました', 'success');
        switchTab('s2', { forceReload: true });
      },
    });
    return ctrl;
  });
}

async function openS4CreateSubscreen(opts = {}) {
  const { mountS4CreateForm } = await import('./mobile-s4-create.js');
  const initialClient = opts.initialClient || null;

  openSubscreen('タスクを作る', (bodyEl) => {
    const ctrl = mountS4CreateForm(bodyEl, {
      currentMember,
      initialClient,
      onSaved: () => {
        closeSubscreen();
        showToast('タスクを保存しました', 'success');
        switchTab('s4');
      },
    });
    return ctrl;
  });
}

/** Phase 2-B-3: S4 一覧からの編集サブ画面 (編集モード + 削除) */
async function openS4EditSubscreen(task) {
  const { mountS4CreateForm } = await import('./mobile-s4-create.js');

  openSubscreen('タスクを編集', (bodyEl) => {
    const ctrl = mountS4CreateForm(bodyEl, {
      currentMember,
      initialTask: task,
      onSaved: () => {
        closeSubscreen();
        showToast('タスクを更新しました', 'success');
        switchTab('s4');
      },
      onDeleted: () => {
        closeSubscreen();
        showToast('タスクを削除しました', 'success');
        switchTab('s4');
      },
    });
    return ctrl;
  });
}

// ============================================================
// トースト
// ============================================================

export function showToast(message, type = 'info') {
  const wrap = document.getElementById('mobile-toast-wrap');
  if (!wrap) return;

  const toast = document.createElement('div');
  toast.className = `mobile-toast is-${type}`;
  toast.textContent = message;
  wrap.appendChild(toast);

  requestAnimationFrame(() => {
    toast.classList.add('is-visible');
  });

  setTimeout(() => {
    toast.classList.remove('is-visible');
    setTimeout(() => {
      if (toast.parentNode) toast.parentNode.removeChild(toast);
    }, 200);
  }, 2400);
}
